/**
 * The airship's walking data as the prototype's builders read it (ADR 0033):
 * brad-airship's `airship-layout.js` handed every builder an `area` (its
 * surfaces, colliders, portals and rooms, in three.js axes: x toward the bow,
 * z south) and every room builder a `room` (its size, doors, walls and
 * workstations in the room's own space). The lifted builders keep reading
 * those shapes; `shipArea` makes them from a scene record and its rooms'
 * layout (`@ab/scene` `shipLayout`), mapping the game's (x, y) to three's
 * (x, −y): one home for the facts, the record and the room grid. A room's
 * workstations are its type's template (`rules/ship_rooms.yaml` `fixtures`,
 * the room's own space), never the scene's interactables, so a room drawn
 * beside the walked one (the interior's neighbour) is furnished all the same;
 * their ids are the placed interactables' (`<room>_<id>`).
 */
import type { Scene, ShipRoomRules } from '@ab/content-schema';
import type { ShipRoomLayout } from '@ab/scene';

export type Side = 'north' | 'east' | 'south' | 'west';

/** A walkable surface in three's axes (z = −y): a rect, or a polygon of [x, z]. */
export type AreaSurface = {
  readonly id: string;
  readonly x0: number;
  readonly x1: number;
  readonly z0: number;
  readonly z1: number;
  readonly y: number;
  readonly polygon?: readonly (readonly [number, number])[];
};

export type AreaCollider = { readonly id: string; readonly kind: 'rail' | 'fixture' | 'wall'; readonly x0: number; readonly x1: number; readonly z0: number; readonly z1: number; readonly height: number; readonly side?: Side };
export type AreaPortal = { readonly id: string; readonly x: number; readonly z: number; readonly radius: number };

/** A workstation in a room's own space, by the prototype's kind names (`interactables.json`). */
export type RoomFixtureKind = 'incubator' | 'hatch-spot' | 'plot' | 'potion-bench' | 'drying-rack' | 'watering-can' | 'wheel' | 'console' | 'seat' | 'chart-table' | 'flag-locker';
export type RoomFixture = {
  readonly id: string;
  readonly kind: RoomFixtureKind;
  readonly x: number;
  readonly z: number;
  readonly pos: { readonly x: number; readonly z: number };
  readonly w: number;
  readonly d: number;
  readonly h: number;
  /** An incubator's or a plot's number in its room (the record's `index`). */
  readonly index: number;
};
export type RoomDoor = { readonly id: string; readonly side: Side; readonly offset: number; readonly width: number; readonly x: number; readonly z: number };

/** A room as `buildPrefab` received it: unturned, in its own space (origin its centre, x along its width, z along its depth, south +z). */
export type RoomBuild = {
  readonly id: string;
  readonly type: string;
  readonly level: number;
  readonly prefab: string;
  readonly rotation: number;
  readonly width: number;
  readonly depth: number;
  readonly size: readonly [number, number];
  readonly center: { readonly x: number; readonly z: number };
  readonly y: number;
  readonly localSurfaces: readonly AreaSurface[];
  /** The walls (split around the doors) and the solid workstations. */
  readonly localColliders: readonly AreaCollider[];
  readonly localDoors: readonly RoomDoor[];
  readonly localInteractions: readonly RoomFixture[];
  readonly localSockets: readonly { readonly id: string; readonly pos: { readonly x: number; readonly z: number }; readonly y: number }[];
};

export type ShipArea = {
  readonly id: 'deck' | 'interior';
  readonly surfaces: readonly AreaSurface[];
  readonly colliders: readonly AreaCollider[];
  readonly portals: readonly AreaPortal[];
  readonly rooms: readonly RoomBuild[];
  /** The companionway hatches below deck: every room's door that is a passage to another deck (`ShipPassage` `hatch`), not a doorway between rooms. */
  readonly hatches: readonly AreaPortal[];
  readonly bounds: { readonly minX: number; readonly maxX: number; readonly minZ: number; readonly maxZ: number };
};

type Template = ShipRoomRules[string]['fixtures'][number];

const fixtureKind = (i: Template): RoomFixtureKind | null => {
  switch (i.kind) {
    case 'incubator':
      return 'incubator';
    case 'hatch_spot':
      return 'hatch-spot';
    case 'plot':
      return 'plot';
    case 'bench':
      return 'potion-bench';
    case 'rack':
      return 'drying-rack';
    case 'fly':
      return 'wheel';
    case 'fixture':
      switch (i.fixture) {
        case 'console':
          return 'console';
        case 'seat':
          return 'seat';
        case 'chart_table':
          return 'chart-table';
        case 'flag_locker':
          return 'flag-locker';
        case 'watering_can':
          return 'watering-can';
        case undefined:
          return null;
      }
      return null;
    case 'talk':
    case 'read':
    case 'wish':
    case 'door':
    case 'shop':
    case 'board':
      return null;
  }
};

const surfaceOf = (s: Scene['surfaces'][number]): AreaSurface | null => {
  if (s.polygon !== undefined) {
    const xs = s.polygon.map((p) => p[0]);
    const zs = s.polygon.map((p) => -p[1]);
    return { id: s.id, x0: Math.min(...xs), x1: Math.max(...xs), z0: Math.min(...zs), z1: Math.max(...zs), y: s.h, polygon: s.polygon.map(([x, y]) => [x, -y] as const) };
  }
  if (s.x0 === undefined || s.x1 === undefined || s.y0 === undefined || s.y1 === undefined) return null;
  return { id: s.id, x0: s.x0, x1: s.x1, z0: -s.y1, z1: -s.y0, y: s.h };
};

/** One room as its builder reads it: the layout's walls, doors and floor, and its type's workstations, in the room's own space. */
export const roomBuild = (r: ShipRoomLayout): RoomBuild => {
  const hw = r.width / 2;
  const hd = r.depth / 2;
  const e = r.extent;
  const fixtures: RoomFixture[] = [];
  const solidIds = new Set<string>();
  for (const t of r.type.fixtures) {
    const kind = fixtureKind(t);
    if (kind === null) continue;
    const id = `${r.room.id}_${t.id}`;
    fixtures.push({ id, kind, x: t.x, z: -t.y, pos: { x: t.x, z: -t.y }, w: t.size.w, d: t.size.d, h: t.size.h, index: t.index ?? 0 });
    if (t.solid !== false) solidIds.add(id);
  }
  const walls: AreaCollider[] = r.walls.map((w) => ({ id: w.id, kind: 'wall', side: w.side, x0: w.x0, x1: w.x1, z0: -w.y1, z1: -w.y0, height: w.height }));
  const solid: AreaCollider[] = fixtures
    .filter((f) => f.w > 0 && f.d > 0 && solidIds.has(f.id))
    .map((f) => ({ id: f.id, kind: 'fixture', x0: f.x - f.w / 2, x1: f.x + f.w / 2, z0: f.z - f.d / 2, z1: f.z + f.d / 2, height: f.h }));
  return {
    id: r.room.id,
    type: r.room.type,
    level: r.room.level,
    prefab: r.level.prefab,
    rotation: r.room.rotation,
    width: r.width,
    depth: r.depth,
    size: r.room.size,
    center: { x: e.cx, z: -e.cy },
    y: e.h,
    localSurfaces: [{ id: `${r.room.id}_floor`, x0: -hw, x1: hw, z0: -hd, z1: hd, y: 0 }],
    localColliders: [...walls, ...solid],
    localDoors: r.doors.map((d) => ({ id: d.id, side: d.side, offset: d.offset, width: d.width, x: d.x, z: -d.y })),
    localInteractions: fixtures,
    localSockets: r.type.sockets.map((s) => ({ id: s.id, pos: { x: s.x, z: -s.y }, y: s.h })),
  };
};

/** A ship scene as the prototype's builders read it: its surfaces, colliders (named ones and rails), portals and rooms, in three's axes. */
export const shipArea = (scene: Scene, rooms: readonly ShipRoomLayout[]): ShipArea => {
  const b = scene.bounds;
  return {
    id: scene.kind === 'ship_deck' ? 'deck' : 'interior',
    surfaces: scene.surfaces.map(surfaceOf).filter((s): s is AreaSurface => s !== null),
    colliders: scene.colliders.flatMap((c, i): AreaCollider[] => [{ id: c.id ?? `collider_${i}`, kind: c.kind ?? 'fixture', x0: c.x0, x1: c.x1, z0: -c.y1, z1: -c.y0, height: c.height ?? 1 }]),
    portals: scene.portals.map((p) => ({ id: p.id, x: p.x, z: -p.y, radius: p.radius })),
    rooms: rooms.map(roomBuild),
    hatches: rooms.flatMap((r) => r.passages.filter((p) => p.kind === 'hatch' && p.to !== null).map((p) => ({ id: p.id, x: p.portal.x, z: -p.portal.y, radius: p.radius }))),
    bounds: { minX: b.x0, maxX: b.x1, minZ: -b.y1, maxZ: -b.y0 },
  };
};
